// src/components/FormSettings/FormSubmitSettings/DraftSubmissionTable.jsx
/* ============================================================================
 * DraftSubmissionTable  (2025-05-12 sync patch)
 * ----------------------------------------------------------------------------
 *  Compact read-only table used inside the form-builder preview.
 *
 *  • Mirrors ColumnDraftManager’s columnOrder / visibility
 *  • Rebuilds from *live* layout + metadata so deleted fields vanish
 *  • Keeps designer’s manual order & visibility flags
 *  • Resizing logic unchanged
 *  • Shows up to 5 rows (stub rows if none yet)
 *  • Uses draftSubmissionTable.module.css for fonts & zebra striping
 * ========================================================================= */

import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { debounce } from 'lodash';                // ← install lodash if you don’t have it

import { useFieldMetadata }           from '../../../contexts/FieldMetadataContext';
import { useFormLayout }              from '../../../contexts/FormLayoutContext';
import { useFormSubmissionSettings }  from '../../../contexts/FormSubmissionSettingsContext';
import { useFormSubmissions }         from '../../../contexts/FormSubmissionsContext';

import styles from './draftSubmissionTable.module.css';
import { isSystemId, resolveSystemValue } from '../../../utils/systemColumns';
import {
  getProviderColumnDisplayState,
} from '../../../utils/providerOutputValueUtils';
import {
  getProviderColumnDefinition,
} from '../../../integrations/providerColumns';

/* ---------------------------------------------------------------- helpers */
const MIN_COL_WIDTH     = 25;
const DEFAULT_COL_WIDTH = 120;

/* Build a column list from the *current* layout + metadata */
const buildFromLayout = (layoutConfig = {}, meta = {}) =>
  Object.values(layoutConfig)
    .flat()
    .filter((f) => f.type !== 'submit')
    .map((f, idx) => ({
      id     : meta[f.id]?.ufi   ?? f.id,
      label  : meta[f.id]?.label ?? `Field ${idx + 1}`,
      visible: true,
      order  : idx,
    }));

/* Merge a fresh list into a base list, preserving order & visibility */
const mergeIntoBase = (base = [], fresh = []) => {
  const next = [...base];

  /* a. rename existing + add new */
  fresh.forEach((f) => {
    const i = next.findIndex((c) => c.id === f.id);
    if (i === -1)
      next.push({ ...f, order: next.length });           // brand-new field
    else
      next[i] = { ...next[i], label: f.label };           // rename
  });

  /* b. drop deleted */
  const filtered = next.filter(
   (c) => c.isCalculated || fresh.some((f) => f.id === c.id)
  );
  
  /* c. re-index order */
  return filtered.map((c, i) => ({ ...c, order: i }));
};

/* ---------------------------------------------------------------- component */
 const DraftSubmissionTable = ({
   columnOrder   = [],
   columnWidths  = {},
   stickyHeader  = false,
   getAuthToken  = null,
 }) => {

  /* Stabilize “empty” identities so props don’t change every render */
  const EMPTY_OBJ = React.useMemo(() => ({}), []);
  const EMPTY_ARR = React.useMemo(() => [], []);

  const stableColumnOrder  = columnOrder  || EMPTY_ARR;
  const stableColumnWidths = columnWidths || EMPTY_OBJ;

  /* Stable token getter for preview priming effect */
  const tokenGetter = useCallback(
    typeof getAuthToken === 'function' ? getAuthToken : async () => null,
    [getAuthToken]
  );

  /* 1️⃣  data & context */

  const { fieldsMetadata }           = useFieldMetadata();
  const { layoutConfig }             = useFormLayout();                    
  const {
    submissions       = [],
    userSubmissions   = [],
  } = useFormSubmissions();
  const {
    submissionSettings,
    updateTableSurfaceSettings,
  } = useFormSubmissionSettings();


  /* -----------------------------------------------------------------------
   * debounced saver — one instance for the lifetime of this component
   *   – 350 ms “quiet-period” after the last mouse-up
   *   – never writes directly to Firestore; only to local/session via
   *     FormSubmissionSettingsContext → safeSetItem()
   * -------------------------------------------------------------------- */
  const saveWidthsDebounced = useRef(
    debounce((nextWidths) => {
      console.log('[DraftSubmissionTable] ⏳ debounced columnWidths save:', nextWidths);
      updateTableSurfaceSettings('addToSubmissionsTable', { columnWidths: nextWidths });
    }, 350)
  ).current;

// Normalizes persisted columnOrder from settings so the table
// can treat every item as an object. Keeps any extra keys on
// virtual rows (kind/role/ufi/label/visible/order).
const normalizeColPrefs = (order = []) => {
  return (Array.isArray(order) ? order : [])
    .filter(Boolean)
    .map((c, i) => {
      if (typeof c === 'string') {
        // string → assume it's an id (e.g., 'thumb@<ufi>')
        return { id: c, visible: true, order: i };
      }
      // object → ensure required fields are present, keep metadata
      return {
        ...c,
        id: String(c.id || ''),
        visible: c.visible !== false,
        order: Number.isFinite(c.order) ? c.order : i,
      };
    });
};


/* 2️⃣  columns – merge designer prefs with live layout */
const columns = useMemo(() => {
  const fresh = buildFromLayout(layoutConfig, fieldsMetadata);

  // Normalize columnOrder coming from settings; may be strings or objects
  const pref = normalizeColPrefs(stableColumnOrder);

  // No designer prefs yet → just use live layout
  if (pref.length === 0) return fresh;

  // Build a quick lookup for labels coming from layout/meta
  const labelById = new Map(
    fresh.map(f => [f.id, f.label])
  );

  // 1) Start from prefs (this preserves order and any virtual rows)
  let base = pref.map((c, i) => ({
    ...c,
    // Only fill label when missing; for virtuals you persisted label already
    label: c.label || labelById.get(c.id) || c.id,
    order: Number.isFinite(c.order) ? c.order : i,
  }));

  // 2) Append any brand-new real fields that aren’t in prefs (defensive)
  const baseIds = new Set(base.map(c => c.id));
  fresh.forEach(f => {
    if (!baseIds.has(f.id)) {
      base.push({
        id: f.id,
        label: f.label,
        visible: true,
        order: base.length,
      });
    }
  });

  // 3) Final stable order
  base = base
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // 4) Ensure system rows are flagged (when prefs didn’t include metadata)
  base = base.map(c =>
    isSystemId(String(c.id))
      ? ({ ...c, kind: c.kind || 'system', role: c.role || 'system' })
      : c
  );

  return base;
}, [stableColumnOrder, layoutConfig, fieldsMetadata]);


  /* 2a) Build a UFI → type map once per render (attach/image detection) */
  const ufiToType = useMemo(() => {
    const map = {};
    try {
      Object.values(fieldsMetadata || {}).forEach((m) => {
        if (m?.ufi) map[m.ufi] = m.type || 'text';
      });
    } catch (e) {
      console.warn('[DraftSubmissionTable] Failed to build ufiToType map:', e);
    }
    return map;
  }, [fieldsMetadata]);


  /* 3️⃣  width map + resize hook (unchanged) */
const initialWidths = useMemo(
  () =>
    columns.reduce((acc, c) => {
      const incoming = stableColumnWidths?.[c.id];
      return { ...acc, [c.id]: incoming ?? DEFAULT_COL_WIDTH };
    }, {}),
  [columns, stableColumnWidths]
);


  const [colWidths,   setColWidths]   = useState(initialWidths);
  const [localWidths, setLocalWidths] = useState(initialWidths);
  const [resizing,    setResizing]    = useState(null);   // { columnId, startX, startWidth } | null
  const [isResizing,  setIsResizing]  = useState(false);

  // ---- lightweight asset preview cache (id -> { previewUrl, meta }) ----
  const previewCacheRef = useRef(new Map());
  const [previewTick, setPreviewTick] = useState(0);

const primePreviews = useCallback(async (assetIds, getToken) => {
  // Filter only the ids we don't have cached yet
  const missing = assetIds.filter(id => id && !previewCacheRef.current.has(id));
  if (!missing.length) return;

  // Resolve a token (caller can pass a getter; keep it defensive)
  const token = await getToken?.();
  if (!token) return;

  try {
    // Dynamic import to keep bundle size lean
    const { fetchAssetById } = await import('../../../utils/frontend_assetManager');

    for (const id of missing) {
      try {
        // Request signed URL + minimal meta for the small table cell.
        // ⬇️ IMPORTANT: prefer:'micro' asks the server to pick the 32/64px thumbnails
        // (You’ll also need getAssetById on the server to honor this hint – see note below.)
        const data = await fetchAssetById(id, token, {
          includeUrls: true,
          dpr: (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1,
          includeMeta: true,
          prefer: 'micro', // ← NEW: prefer 32/64 "micro" renditions instead of 400/800 grid
        });

        // Cache the previewUrl/meta for this assetId
        previewCacheRef.current.set(id, {
          previewUrl: data.previewUrl || null,
          meta: data.meta || null,
        });
      } catch (err) {
        // Non-fatal: cache a null entry so we don’t retry immediately for this id
        previewCacheRef.current.set(id, { previewUrl: null, meta: null });
      }
    }

    // Nudge a repaint once the cache has been populated
    setPreviewTick(n => n + 1);
  } catch (outerErr) {
    // swallow dynamic import errors (should be rare)
  }
}, []);


  /* Log-on-change: only when columns signature actually changes */
  const lastColsSigRef = useRef('');
  useEffect(() => {
    if (isResizing) return; // skip noisy logs during drag
    const sig = columns
      .map(c => `${c.id}:${c.order}:${c.visible !== false ? 1 : 0}`)
      .join('|');
    if (sig !== lastColsSigRef.current) {
      console.log(
        '[DraftSubmissionTable] columns after merge:',
        columns.map(c => ({
          id: c.id,
          isCalc: !!c.isCalculated,
          order: c.order,
        }))
      );
      lastColsSigRef.current = sig;
    }
  }, [columns, isResizing]);

 
  /* --- keep width map in sync if columns added later --- */
  useEffect(() => {
    setColWidths((prev) => {
    const next = { ...prev };

    /* add missing IDs */
    columns.forEach((c) => {
      if (!next[c.id]) next[c.id] = DEFAULT_COL_WIDTH;
    });

    /* prune widths whose column was deleted */
    Object.keys(next).forEach((id) => {
      if (!columns.some((c) => c.id === id)) delete next[id];
    });
    return next;

    });
  }, [columns]);

/* --- keep colWidths in sync with prop columnWidths (when designer revisits) --- */
useEffect(() => {
  if (!stableColumnWidths) return;
  setColWidths((prev) => ({ ...prev, ...stableColumnWidths }));
  setLocalWidths((prev) => ({ ...prev, ...stableColumnWidths }));
}, [stableColumnWidths]);


  /* --- handlers – identical to live SubmissionTable ---- */
  const startResizing = (columnId, evt) => {
    if (isResizing) return;

    setResizing({
      columnId,
      startX:    evt.clientX,
      startWidth: localWidths[columnId] || DEFAULT_COL_WIDTH,
    });
    setIsResizing(true);
  };

  const handleMouseMove = (evt) => {
    if (!resizing) return;
    const { columnId, startX, startWidth } = resizing;
    const deltaX = evt.clientX - startX;
    const newWidth = Math.max(MIN_COL_WIDTH, startWidth + deltaX);

    setLocalWidths((prev) =>
      prev[columnId] === newWidth ? prev : { ...prev, [columnId]: newWidth }
    );

    /* live-update cell DOM width so designer sees it immediately */
    const header = document.querySelector(`[data-col='${columnId}']`);
    const cells  = document.querySelectorAll(`[data-col='${columnId}-body']`);
    if (header) {
      header.style.width    = `${newWidth}px`;
      header.style.minWidth = `${MIN_COL_WIDTH}px`;
    }
    cells.forEach((td) => {
      td.style.width    = `${newWidth}px`;
      td.style.minWidth = `${MIN_COL_WIDTH}px`;
    });
  };

  const stopResizing = () => {
    if (!resizing) return;
    const { columnId } = resizing;

  /* ------------------------------------------------------------------
   * 1) Build a single “nextWidths” map that already includes the resize
   *    AND excludes any columns that no longer exist (prune step below).
   * ------------------------------------------------------------------ */
  const nextWidthsUnpruned = {
    ...colWidths,               // ← current map
    [columnId]: localWidths[columnId],
  };

  /* prune widths whose column was deleted (keeps storage tidy) */
  const colIds      = new Set(columns.map((c) => c.id));
  const nextWidths  = Object.fromEntries(
    Object.entries(nextWidthsUnpruned).filter(([id]) => colIds.has(id))
  );

  /* 2) Update local state **once** with the pruned map */
  setColWidths(nextWidths);

  /* 3) Buffer the write — localStorage will update after the debounce
   *    interval **only if the designer stops resizing for 350 ms**.
   */
  saveWidthsDebounced(nextWidths);


    setResizing(null);
    setIsResizing(false);
  };

  /* attach / detach mousemove listener */
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup',   stopResizing);
      document.addEventListener('blur',      stopResizing);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup',   stopResizing);
      document.removeEventListener('blur',      stopResizing);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup',   stopResizing);
      document.removeEventListener('blur',      stopResizing);
    };
  }, [isResizing, resizing, localWidths]);

  /* ---------- cleanup: cancel any pending debounce on unmount ---------- */
  useEffect(() => {
    return () => {
      if (saveWidthsDebounced.cancel) saveWidthsDebounced.cancel();
    };
  }, [saveWidthsDebounced]);

/* choose the viewer-only slice (always) – table must only reflect this viewer's session */
const srcRows = userSubmissions;

// ─────────────────────────────────────────────────────────────
// Enterprise ordering: always show newest submissions on top.
// Draft mode rows come from session storage and may be appended,
// so we must sort by timestamp here to guarantee correct UX.
// Supports:
//  • Firestore Timestamp (toDate())
//  • Date
//  • ISO string
//  • missing → 0
// Preference:
//  • submittedAt → createdAt → 0
// ─────────────────────────────────────────────────────────────
const toMs = (t) => {
  try {
    if (!t) return 0;
    if (typeof t?.toDate === 'function') return t.toDate().getTime(); // Firestore Timestamp
    if (t instanceof Date) return t.getTime();
    const ms = new Date(t).getTime(); // ISO string or other date-like
    return Number.isFinite(ms) ? ms : 0;
  } catch (_) {
    return 0;
  }
};

const newestFirst = useMemo(() => {
  const rows = Array.isArray(srcRows) ? srcRows : [];
  // Sort by submittedAt desc, fallback to createdAt desc
  return rows
    .slice()
    .sort((a, b) => {
      const aMs = toMs(a?.submittedAt) || toMs(a?.createdAt);
      const bMs = toMs(b?.submittedAt) || toMs(b?.createdAt);
      return bMs - aMs;
    });
}, [srcRows]);

  /* ---------------------------------------------------------------------
   *  Ordering
   *  Published forms already arrive newest → oldest from Firestore.
   *  Draft/testing rows are local and may not be ordered, so we sort above.
   * ------------------------------------------------------------------- */

  /* DEBUG */
  /* show max 5 rows (or stub rows) */
  const displayRows = newestFirst.length
    ? newestFirst.slice(0, 5)            // ← newest submissions on top
    : Array.from({ length: 5 }).map(() => null);  // null → stub row


  /* DEBUG (log-on-change) */
  const lastRowSnapRef = useRef({ total: -1, viewerOnly: -1, used: -1 });
  useEffect(() => {
    const snap = {
      total: submissions.length,
      viewerOnly: userSubmissions.length,
      used: newestFirst.length,
    };
    const prev = lastRowSnapRef.current;
    if (
      prev.total !== snap.total ||
      prev.viewerOnly !== snap.viewerOnly ||
      prev.used !== snap.used
    ) {
      console.log('[DraftSubmissionTable] rows available:', snap);
      lastRowSnapRef.current = snap;
    }
  }, [submissions.length, userSubmissions.length, newestFirst.length]);

  /* -----------------------------------------------------------------------
   * Linked-assets per-row helper
   *  - The server writes a JSON string at `_linkedAssetsJson` with entries:
   *      [{ ufi, assetId, versionNumber, assetType, width, height,
   *         previewUrl, tags[], aufResponses{...} }, ...]
   *  - We parse it once per row and cache by row object using WeakMap.
   *  - Returns a Map<attachUfi, linkedEntry>.
   * --------------------------------------------------------------------- */
  const linkedMapByRowRef = useRef(new WeakMap());
  const getLinkedForRow = useCallback((row) => {
    if (!row || typeof row !== 'object') return null;
    const cache = linkedMapByRowRef.current.get(row);
    if (cache) return cache;

    const raw = row?._linkedAssetsJson;
    if (!raw || typeof raw !== 'string') {
      linkedMapByRowRef.current.set(row, null);
      return null;
    }
    try {
      const arr = JSON.parse(raw);
      const map = new Map();
      if (Array.isArray(arr)) {
        arr.forEach((entry) => {
          if (entry && typeof entry === 'object' && entry.ufi) {
            map.set(entry.ufi, entry);
          }
        });
      }
      linkedMapByRowRef.current.set(row, map);
      return map;
    } catch (e) {
      console.warn(
        '[DraftSubmissionTable] _linkedAssetsJson parse failed:',
        e?.message || e
      );
      linkedMapByRowRef.current.set(row, null);
      return null;
    }
  }, []);

  // Prime previews whenever a Thumbnail virtual OR a base attach/image column is visible
  useEffect(() => {
    const visibleCols = columns.filter(c => c.visible !== false);

    // A) virtual 'thumb@<ufi>' columns
    const thumbCols = visibleCols.filter(
      c => (c.kind === 'virtual' && c.role === 'thumb') ||
           String(c.id).startsWith('thumb@')
    );

    // B) base attach/image columns (id is the UFI)
    const baseAssetCols = visibleCols.filter(c => {
      const t = ufiToType[c.id];
      return t === 'attach' || t === 'image';
    });

    if (!thumbCols.length && !baseAssetCols.length) return;

    const ids = [];
    for (const row of displayRows) {
      if (!row) continue;
      // virtual thumbs
      for (const col of thumbCols) {
        const ufi = col.ufi || String(col.id).split('@')[1];
        if (!ufi) continue;
        const aid = extractFirstAssetId(row, ufi);
        if (aid) ids.push(aid);
      }
      // base asset fields
      for (const col of baseAssetCols) {
        const ufi = col.id;
        const aid = extractFirstAssetId(row, ufi);
        if (aid) ids.push(aid);
      }
    }

   primePreviews([...new Set(ids)], tokenGetter);
  }, [displayRows, columns, previewTick, ufiToType, tokenGetter, primePreviews]);


  /* Reuse the same visible/sorted columns for header & body */
  const visibleSortedColumns = useMemo(() => {
    return columns
      .filter((c) => c.visible !== false)
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [columns]);

  /**
   * unwrapValue – defensive un-wrapper for common shapes
   * Accepts x, returns the inner thing:
   *   { value: { assetId: '...' } }  → { assetId: '...' }
   *   { value: { value: 'abc' } }    → { value: 'abc' }
   *   primitive/array/object         → itself
   */
  const unwrapValue = (x) => {
    let curr = x;
    // Unwrap at most twice to avoid accidental loops
    for (let i = 0; i < 2; i++) {
      if (curr && typeof curr === 'object' && 'value' in curr && !Array.isArray(curr)) {
        curr = curr.value;
      } else {
        break;
      }
    }
    return curr;
  };

  /**
   * extractFirstAssetId – returns the first assetId for a given attach/image UFI in a row.
   * Uses unwrapValue to normalize and supports allowMultiple arrays.
   */
  const extractFirstAssetId = (row, ufi) => {
    const cell = row?.responses?.[ufi];
    const raw  = (cell && typeof cell === 'object' && 'value' in cell) ? cell.value : cell;
    const x    = unwrapValue(raw);
    if (Array.isArray(x)) {
      const first = x.find(it => it && typeof it === 'object' && (it.assetId || it.id));
      return first ? (first.assetId || first.id) : null;
    }
    if (x && typeof x === 'object') return x.assetId || x.id || null;
    return null;
  };


  /**
   * extractAssetish – returns a human string for asset-ish values.
   * Handles arrays, single objects, and primitives.
   * Prefers assetId → id → (string) value → JSON string.
   */
  const extractAssetish = (raw) => {
    const x = unwrapValue(raw);

    if (Array.isArray(x)) {
      return x
        .map((item) => extractAssetish(item))
        .filter((s) => s !== '')
        .join(', ');
    }

    if (x && typeof x === 'object') {
      // If still wrapped, unwrap inner once more for asset-ish detection
      const inner = unwrapValue(x);
      const id = inner?.assetId || inner?.id ||
                 (typeof inner?.value === 'string' ? inner.value : '');
      if (id) return String(id);
      // Final cheap fallback for unexpected shapes:
      return '';

    }

    return x == null ? '' : String(x);
  };

  /* Helper: render per-cell display value with special handling for attach/image */
  const renderCellValue = (ufi, row, stubIndex) => {
    const fieldType = ufiToType[ufi] || 'text';

    // STUB rows (no data yet)
    if (!row) {
      if (fieldType === 'attach' || fieldType === 'image') {
        // show gray box stub for image cells
        return (
          <span
            style={{
              width: 18, height: 18, display: 'inline-block',
              background: '#eee', borderRadius: 2
            }}
          />
        );
      }
      return stubIndex === 0 ? 'Example' : '';
    }

    // Real data
    const cell = row?.responses?.[ufi];
    const raw  = (cell && typeof cell === 'object' && 'value' in cell)
      ? cell.value : cell;

    // Base attach/image → show the thumbnail image (not ID)
    if (fieldType === 'attach' || fieldType === 'image') {
      const aid = extractFirstAssetId(row, ufi);
      if (!aid) {
        return (
          <span
            style={{
              width: 18, height: 18, display: 'inline-block',
              background: '#eee', borderRadius: 2
            }}
          />
        );
      }
      const cached = previewCacheRef.current.get(aid);
      const url = cached?.previewUrl || null;
      if (url) {
        return (
          <img
            src={url}
            alt=""
            width={18}
            height={18}
            style={{ objectFit: 'cover', borderRadius: 2 }}
            title={aid}
            aria-label="asset thumbnail"
          />
        );
      }
      return (
        <span
          title={aid}
          aria-label="asset thumbnail loading"
          style={{
            width: 18, height: 18, display: 'inline-block',
            background: '#eee', borderRadius: 2
          }}
        />
      );
    }

    // Other field types → your original scalar logic
    const normalized = unwrapValue(raw);
    if (normalized == null) return '';
    if (typeof normalized === 'object') {
      return normalized.value != null && typeof normalized.value !== 'object'
        ? String(normalized.value)
        : '';
    }
    return String(normalized);
  };


  // ---- virtual column renderers ----
  const renderThumbCell = (row, col) => {
    const ufi = col.ufi || String(col.id).split('@')[1];
    if (!ufi) return '';
    const aid = extractFirstAssetId(row, ufi);
    if (!aid) return '';

    const cached = previewCacheRef.current.get(aid);
    const url = cached?.previewUrl || null;
// Show only the image (thumb). Keep the assetId in a tooltip/title.
// If the URL isn't ready yet, render a gray square placeholder.
if (url) {
  return (
    <img
      src={url}
      alt=""
      width={18}
      height={18}
      style={{ objectFit: 'cover', borderRadius: 2 }}
      title={aid}              // hover shows the id without rendering it in-cell
      aria-label="asset thumbnail"
    />
  );
}
return (
  <span
    title={aid}
    aria-label="asset thumbnail loading"
    style={{
      width: 18,
      height: 18,
      display: 'inline-block',
      background: '#eee',
      borderRadius: 2
    }}
  />
);

  };

  const renderVersionCell = (row, col) => {
  const ufi = col.ufi || String(col.id).split('@')[1];
  if (!ufi) return '';

  // 1) Prefer server-enriched link (authoritative)
  const linked = getLinkedForRow(row);
  const linkedEntry = linked?.get(ufi) || null;
  if (linkedEntry && typeof linkedEntry.versionNumber !== 'undefined') {
    return String(linkedEntry.versionNumber ?? '');
  }

  // 2) Fallback to attach cell (may carry versionNumber or version:"thumbnail")
  const cell = row?.responses?.[ufi];
  const raw  = (cell && typeof cell === 'object' && 'value' in cell)
    ? cell.value : cell;
  const x    = unwrapValue(raw);

  if (Array.isArray(x)) {
    const first = x.find(it =>
      it && typeof it === 'object' && (it.assetId || it.id)
    );
    return String(first?.versionNumber ?? first?.version ?? '');
  }

  if (x && typeof x === 'object') {
    return String(x.versionNumber ?? x.version ?? '');
  }

  return '';
};


const renderAssetTypeCell = (row, col) => {
  const ufi = col.ufi || String(col.id).split('@')[1];
  if (!ufi) return '';

  // 1) Prefer server-enriched link
  const linked = getLinkedForRow(row);
  const linkedEntry = linked?.get(ufi) || null;
  if (linkedEntry && typeof linkedEntry.assetType !== 'undefined') {
    return String(linkedEntry.assetType ?? '');
  }

  // 2) Fallback to attach cell
  const cell = row?.responses?.[ufi];
  const raw  = (cell && typeof cell === 'object' && 'value' in cell)
    ? cell.value : cell;
  const x    = unwrapValue(raw);

  if (Array.isArray(x)) {
    const first = x.find(it =>
      it && typeof it === 'object' && (it.assetId || it.id)
    );
    return String(first?.assetType ?? '');
  }

  if (x && typeof x === 'object') {
    return String(x.assetType ?? '');
  }

  return '';
};


  const renderAufFieldCell = (row, col) => {
    // Virtual AUF col id conventions:
    //  • explicit: { kind:'virtual', role:'aufField', ufi:'<attachUfi>', aufUfi:'<ufi>' }
    //  • id-based: "aufField@<attachUfi>@<aufUfi>"
    const attachUfi = col.ufi   || String(col.id).split('@')[1];
    const aufUfi    = col.aufUfi|| String(col.id).split('@')[2];
    if (!attachUfi || !aufUfi) return '';

    // 1) Prefer server-enriched linkedAssetsJson (authoritative)
    const linked = getLinkedForRow(row);
    const linkedEntry = linked?.get(attachUfi) || null;
    if (linkedEntry && linkedEntry.aufResponses) {
      const v = linkedEntry.aufResponses[aufUfi];
      if (v != null && typeof v !== 'object') {
        return String(v);
      }
      // array support if you ever store arrays on AUF
      if (Array.isArray(v)) return v.filter(Boolean).join(', ');
    }

    // 2) Fallback: preview meta (from fetchAssetById cache)
    const aid = extractFirstAssetId(row, attachUfi);
    if (!aid) return '';
    const cached = previewCacheRef.current.get(aid);
    const meta = cached?.meta || null;
    if (meta) {
      // If you ever copy promoted fields onto meta top-level keys:
      if (aufUfi in meta && typeof meta[aufUfi] !== 'object') {
        return String(meta[aufUfi] ?? '');
      }
      // Otherwise use meta.responses[aufUfi]
      const rv = meta?.responses?.[aufUfi]?.value;
      if (Array.isArray(rv)) return rv.filter(Boolean).join(', ');
      if (rv != null && typeof rv !== 'object') return String(rv);
    }

    return '';
  };


  // Asset ID virtual column – print first asset id for this attach/image UFI
  const renderAssetIdCell = (row, col) => {
    // Supports both explicit metadata (col.ufi) and id scheme "assetId@<ufi>"
    const ufi = col.ufi || String(col.id).split('@')[1];
    if (!ufi) return '';
    return extractFirstAssetId(row, ufi) || '';
  };

  const renderProviderColumnCell = (row, col, rowIndex = 0) => {
    if (!row) {
      // Builder/stub rows should show the column shape without implying that
      // backend enrichment has run. Real pending text is reserved for actual
      // submitted rows with runtime/backend state.
      //
      // Builder-preview UX rule:
      // - Match normal field preview behavior by showing sample copy only in
      //   the first stub row.
      // - Keep remaining blank preview rows empty so the table does not look
      //   like multiple shortlinks have already been generated.
      // - Do not show "Getting URL..." in builder preview because no backend
      //   shortlink job has been created yet.
      if (col?.role !== 'providerOutput') {
        return '';
      }

      return rowIndex === 0 ? 'example shortlink' : '';
    }

    const definition = getProviderColumnDefinition(col);
    const state = getProviderColumnDisplayState(row, col, definition);

    if (!state?.displayText) {
      return '';
    }

    if (state.isPending) {
      return (
        <span
          title={state.status || ''}
          style={{ fontStyle: 'italic', opacity: 0.72 }}
        >
          {state.displayText}
        </span>
      );
    }

    if (state.isError) {
      return (
        <span title={state.error || state.displayText}>
          {state.displayText}
        </span>
      );
    }

    return state.displayText;
  };

   const virtualRenderers = {
     thumb    : renderThumbCell,
     version  : renderVersionCell,
     assetType: renderAssetTypeCell,
     aufField : renderAufFieldCell,
    assetId  : renderAssetIdCell,
    providerOutput: renderProviderColumnCell,
    providerMetric: renderProviderColumnCell,
   };



  /* 5️⃣  render */
  return (
    <Paper elevation={0} sx={{ mt: 2, p: 0, width: '100%' }}>
      <TableContainer
        className={styles.tableContainer}
        sx={{ maxHeight: 400, overflowX: 'auto' }}
      >
        <Table
          stickyHeader={stickyHeader}
          size="small"
          sx={{ tableLayout: 'fixed', minWidth: 'max-content' }}   // 🟢 1

        >
          {/* ───────── header ───────── */}
          <TableHead>
            <TableRow>
              {visibleSortedColumns.map((c) => (
                  <TableCell
                    key={c.id}
                    data-col={c.id}                       /* for live width tweak */
                    className={`${styles.headerCell} ${stickyHeader ? styles.stickyHeader : ''}`}
                     sx={{
                       width:    colWidths[c.id],
                       minWidth: MIN_COL_WIDTH,
                      overflow: 'hidden',           // 🟢 2
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                     }}

                  >
                    {c.label}

                    {/* resize handle */}
                    <div
                      className={styles.columnResizer}
                      onMouseDown={(e) => startResizing(c.id, e)}
                    />
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>

          {/* ───────── body ───────── */}
          <TableBody>
            {displayRows.map((row, rIdx) => (
              <TableRow
                key={rIdx}
                className={styles.zebraRow}
              >
{visibleSortedColumns.map((c) => {
    // System → Virtual → Base
    let cellNode;

    // 0) System columns (ids like 'sys:*') render from top-level row fields
    if (c.kind === 'system' || isSystemId(String(c.id))) {
      if (!row) {
        // stub row: keep sparse (match your existing stub behavior)
        cellNode = (rIdx === 0 ? 'Example' : '');
      } else {
        const text = resolveSystemValue(row, c.id); // maps to row.id / row.submittedAt / etc.
        cellNode = text == null ? '' : String(text);
      }

    // 1) Virtual columns (thumb/version/assetType/aufField/assetId/providerOutput/providerMetric)
    } else if (c.kind === 'virtual') {
      const role =
        c.role ||
        (String(c.id).startsWith('thumb@')     ? 'thumb'     :
         String(c.id).startsWith('assetType@') ? 'assetType' :
         String(c.id).startsWith('version@')   ? 'version'   :
         String(c.id).startsWith('aufField@')  ? 'aufField'  :
         String(c.id).startsWith('assetId@')   ? 'assetId'   :
         String(c.id).startsWith('providerOutput@') ? 'providerOutput' :
         String(c.id).startsWith('providerMetric@') ? 'providerMetric' :
         '');
      const fn = virtualRenderers[role];
      cellNode = fn ? fn(row, c, rIdx) : '';

    // 2) Base (regular UFI) fields
    } else {
      cellNode = renderCellValue(c.id, row, rIdx);
    }


                    return (
                      <TableCell
                        key={c.id}
                        data-col={`${c.id}-body`}            /* for live width tweak */
                        className={styles.bodyCell}
                        sx={{                               
                          minWidth: MIN_COL_WIDTH,
                          maxWidth: colWidths[c.id],
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {cellNode}
                      </TableCell>
                    );
                  })}

              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default React.memo(DraftSubmissionTable);
